/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { inject, Injectable } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

import { EmailSenderKindEnum } from '@core/automated-actions-types';
import { I18nService } from '@core/i18n-legacy';
import { DefaultVisibility, Visibility } from '@core/visibility-types';
import {
  AutomationAction,
  AutomationActionType,
  AutomationCondition,
  AutomationConditionDataValue,
  AutomationConditionType,
  AutomationTrigger,
  AutomationTriggerType,
  TriggerCategory,
} from '@core/workflow-automations-types';
import {
  ActionVisibilityHelpers,
  AutomationAccessService,
  AutomationConditionsValidationService,
  AutomationOptionsService,
  AutomationsAccessoryDataService,
  AutomationsValidationService,
  EmailAttachmentsService,
} from '@recruitee/workflow-automations-logic';
import {
  AutomationActionForm,
  AutomationConditionForm,
  AutomationForm,
  ConditionOption,
} from '@recruitee/workflow-automations-types';

@Injectable()
export class AutomationsWizardDialogFormService {
  private readonly fb = inject(FormBuilder);
  private readonly automationsValidationService = inject(AutomationsValidationService);
  private readonly accessoryDataService = inject(AutomationsAccessoryDataService);
  private readonly accessService = inject(AutomationAccessService);
  private readonly emailAttachmentsService = inject(EmailAttachmentsService);
  private readonly automationOptionsService = inject(AutomationOptionsService);
  private readonly actionVisibilityHelpers = inject(ActionVisibilityHelpers);
  private readonly automationConditionsValidationService = inject(
    AutomationConditionsValidationService,
  );
  private readonly i18n = inject(I18nService);

  private readonly defaultVisibility: Visibility = DefaultVisibility;

  public form: AutomationForm = this.fb.group({
    trigger: this.fb.group({
      type: this.fb.control<AutomationTriggerType | null>(null, Validators.required),
      data: this.fb.group({}),
    }),
    conditions: this.fb.array<AutomationConditionForm>([]),
    actions: this.fb.array<AutomationActionForm>([], Validators.required),
  });

  public setTriggerType(type: AutomationTriggerType | null): void {
    this.form.controls.trigger.controls.type.setValue(type);
  }

  public buildTriggerDataForm({ type, data }: Partial<AutomationTrigger>): void {
    const form = this.form.controls.trigger;
    this.removeAllControls(this.form.controls.trigger.controls.data);
    this.accessoryDataService.setTriggerCategory(
      this.automationOptionsService
        .triggerOptions()
        .find(group => group.options.some(option => option.value === type))?.category ||
        TriggerCategory.Candidate,
    );

    const currentTriggerDataOptions = this.automationOptionsService.currentTriggerDataOptions();
    if (currentTriggerDataOptions) {
      currentTriggerDataOptions.forEach(dataOption => {
        const triggerData = data?.find(option => option.name === dataOption.name);

        form.controls.data.setControl(
          dataOption.name,
          this.fb.control(triggerData?.value || null, { validators: Validators.required }),
        );
      });
    } else {
      this.removeAllControls(this.form.controls.trigger.controls.data);
    }
  }

  public addCondition(
    condition: Partial<AutomationCondition>,
  ): FormControl<AutomationConditionType | null> {
    const { type, data } = condition;
    const preparedData =
      data?.reduce(
        (acc, option) => {
          acc[option.name] =
            (option?.selectedOption
              ? option.selectedOption.value
              : option.selectedOptions!.map(option => <string>option.value)) || null;
          return acc;
        },
        <Record<string, AutomationConditionDataValue>>{},
      ) || {};
    const typeControl = this.fb.control(type || null);
    const dataControl = this.fb.nonNullable.group({});

    Object.entries(preparedData).map(([key, value]) => {
      dataControl.setControl(key, this.fb.control(value));
    });

    this.form.controls.conditions.push(
      <AutomationConditionForm>this.fb.nonNullable.group(
        {
          type: typeControl,
          data: dataControl,
        },
        {
          validators: this.automationConditionsValidationService.conditionValidator(),
        },
      ),
    );
    return typeControl;
  }

  public buildConditionDataForm(conditionOption: ConditionOption, controlIndex: number): void {
    const conditionForm = this.form.controls.conditions.at(controlIndex);
    if (!conditionForm) return;

    this.removeAllControls(conditionForm.controls.data);
    conditionOption.options.forEach(dataOption => {
      conditionForm.controls.data.setControl(dataOption.name, this.fb.control(null));
    });
  }

  public addAction(action: Partial<AutomationAction>): void {
    const { type, data, references, uuid } = action;
    if (!type) return;
    const commonActionFields = { uuid, type };
    switch (type) {
      case AutomationActionType.CandidateAddTags:
      case AutomationActionType.JobAddTags:
        this.form.controls.actions.push(
          this.fb.nonNullable.group(
            {
              ...commonActionFields,
              data: this.fb.nonNullable.group({
                tags: this.fb.control(data?.tags || []),
              }),
            },
            {
              validators: this.automationsValidationService.automatedActionValidator(
                this.accessoryDataService.jobItemData(),
              ),
            },
          ),
        );
        break;
      case AutomationActionType.CandidateAddSourceTags:
        this.form.controls.actions.push(
          this.fb.nonNullable.group(
            {
              ...commonActionFields,
              data: this.fb.nonNullable.group({
                sourceTags: this.fb.control(data?.sourceTags || []),
              }),
            },
            {
              validators: this.automationsValidationService.automatedActionValidator(
                this.accessoryDataService.jobItemData(),
              ),
            },
          ),
        );
        break;
      case AutomationActionType.CandidateAddFollowers:
        this.form.controls.actions.push(
          this.fb.nonNullable.group(
            {
              ...commonActionFields,
              data: this.fb.nonNullable.group({
                followerIds: this.fb.control(data?.followerIds || []),
                adminIdsAddRecruiter: data?.adminIdsAddRecruiter || false,
                adminIdsAddHiringManager: data?.adminIdsAddHiringManager || false,
              }),
            },
            {
              validators: this.automationsValidationService.automatedActionValidator(
                this.accessoryDataService.jobItemData(),
              ),
            },
          ),
        );
        break;
      case AutomationActionType.CandidateAddNote:
      case AutomationActionType.JobAddNote:
        this.form.controls.actions.push(
          this.fb.nonNullable.group(
            this.actionVisibilityHelpers.isActionVisible(data?.visibility || this.defaultVisibility)
              ? {
                  ...commonActionFields,
                  data: this.fb.nonNullable.group({
                    body: data?.body || '',
                    bodyHtml: data?.bodyHtml || '',
                    bodyJson: data?.bodyJson || null,
                    visibility: data?.visibility || this.defaultVisibility,
                  }),
                }
              : {
                  ...commonActionFields,
                  data: this.fb.nonNullable.group({
                    visibility: data?.visibility || this.defaultVisibility,
                  }),
                },
            {
              validators: this.automationsValidationService.automatedActionValidator(
                this.accessoryDataService.jobItemData(),
              ),
            },
          ),
        );
        break;
      case AutomationActionType.CandidateAddTask:
        this.form.controls.actions.push(
          this.fb.nonNullable.group(
            {
              ...commonActionFields,
              data: this.fb.nonNullable.group({
                adminIds: this.fb.control(data?.adminIds || []),
                dueDateType: data?.dueDateType || null,
                dueDateUnit: data?.dueDateUnit || null,
                dueDateValue: data?.dueDateValue || null,
                title: data?.title || '',
                timezone: data?.timezone || null,
                adminIdsAddAdminPerformingAction: data?.adminIdsAddAdminPerformingAction || false,
                adminIdsAddHiringManager: data?.adminIdsAddHiringManager || false,
                adminIdsAddRecruiter: data?.adminIdsAddRecruiter || false,
              }),
            },
            {
              validators: this.automationsValidationService.automatedActionValidator(
                this.accessoryDataService.jobItemData(),
              ),
            },
          ),
        );
        break;
      case AutomationActionType.CandidateEvaluationRequest:
        this.form.controls.actions.push(
          this.fb.nonNullable.group(
            {
              ...commonActionFields,
              data: this.fb.nonNullable.group({
                interviewTemplateId: data?.interviewTemplateId || null,
                adminIds: this.fb.control(data?.adminIds || []),
                adminIdsAddRecruiter: data?.adminIdsAddRecruiter || false,
                adminIdsAddHiringManager: data?.adminIdsAddHiringManager || false,
              }),
            },
            {
              validators: this.automationsValidationService.automatedActionValidator(
                this.accessoryDataService.jobItemData(),
              ),
            },
          ),
        );
        break;
      case AutomationActionType.CandidateAssignTo:
        this.form.controls.actions.push(
          this.fb.nonNullable.group(
            {
              ...commonActionFields,
              data: this.fb.nonNullable.group({
                offerIds: this.fb.control(data?.offerIds || []),
                talentPoolIds: this.fb.control(data?.talentPoolIds || []),
              }),
            },
            {
              validators: this.automationsValidationService.automatedActionValidator(
                this.accessoryDataService.jobItemData(),
                references,
              ),
            },
          ),
        );
        break;
      case AutomationActionType.CandidateUnassignFromJob:
        this.form.controls.actions.push(
          this.fb.nonNullable.group(
            {
              ...commonActionFields,
              data: this.fb.nonNullable.group({
                offerIds: this.fb.control(data?.offerIds || []),
                talentPoolIds: this.fb.control(data?.talentPoolIds || []),
                includeSelf: data?.includeSelf || false,
              }),
            },
            {
              validators: this.automationsValidationService.automatedActionValidator(
                this.accessoryDataService.jobItemData(),
                references,
              ),
            },
          ),
        );
        break;
      case AutomationActionType.CandidateSendEmail:
        this.form.controls.actions.push(
          this.fb.nonNullable.group(
            this.actionVisibilityHelpers.isActionVisible(data?.visibility || this.defaultVisibility)
              ? {
                  ...commonActionFields,
                  data: this.fb.nonNullable.group({
                    from:
                      data?.from ||
                      (this.accessoryDataService.isLockedStage()
                        ? this.accessoryDataService.currentUser()!.id
                        : null),
                    fromKind:
                      data?.fromKind ||
                      (this.accessoryDataService.isLockedStage()
                        ? EmailSenderKindEnum.Admin
                        : EmailSenderKindEnum.UserPerformingAction),
                    toAdminIds: this.fb.control(
                      (data?.toAdminIds || <number[]>[]).filter(adminId =>
                        this.accessoryDataService.allowedUsers().find(user => user.id === adminId),
                      ),
                    ),
                    toCandidate: data?.toCandidate !== undefined ? data?.toCandidate : true,
                    toHiringManager: data?.toHiringManager || false,
                    toRecruiter: data?.toRecruiter || false,
                    ccAdminIds: this.fb.control(
                      (data?.ccAdminIds || <number[]>[]).filter(adminId =>
                        this.accessoryDataService.allowedUsers().find(user => user.id === adminId),
                      ),
                    ),
                    ccCandidate: data?.ccCandidate || false,
                    ccHiringManager: data?.ccHiringManager || false,
                    ccRecruiter: data?.ccRecruiter || false,
                    bccAdminIds: this.fb.control(
                      (data?.bccAdminIds || <number[]>[]).filter(adminId =>
                        this.accessoryDataService.allowedUsers().find(user => user.id === adminId),
                      ),
                    ),
                    bccCandidate: data?.bccCandidate || false,
                    bccHiringManager: data?.bccHiringManager || false,
                    bccRecruiter: data?.bccRecruiter || false,
                    subject: data?.subject || '',
                    bodyHtml: data?.bodyHtml || '',
                    signature: data?.signature || false,
                    visibility: data?.visibility || this.defaultVisibility,
                    attachmentIds: this.fb.control(data?.attachmentIds || []),
                    sendAtTimezone: data?.sendAtTimezone ?? null,
                    sendAfterDays: data?.sendAfterDays ?? null,
                    sendAfterAtHour: data?.sendAfterAtHour ?? null,
                    sendAfterAtMinute: data?.sendAfterAtMinute ?? null,
                  }),
                }
              : {
                  ...commonActionFields,
                  data: this.fb.nonNullable.group({
                    visibility: data?.visibility || this.defaultVisibility,
                  }),
                },
            {
              validators: this.automationsValidationService.automatedActionValidator(
                this.accessoryDataService.jobItemData(),
              ),
            },
          ),
        );
        this.emailAttachmentsService.setCurrentAttachments(action?.references?.attachments || []);
        break;
      case AutomationActionType.CandidateCancelScheduledEvents:
        this.form.controls.actions.push(
          this.fb.nonNullable.group(
            {
              ...commonActionFields,
              data: this.fb.nonNullable.group({
                notifyCandidate: data?.notifyCandidate ?? this.accessService.canSendEmails,
                from: data?.from || this.accessoryDataService.currentUser()!.id,
                fromKind: data?.fromKind || EmailSenderKindEnum.Admin,
                subject: data?.subject || '',
                bodyHtml:
                  data?.bodyHtml ||
                  (this.accessService.canSendEmails
                    ? this.i18n.translate('app.interview.delete_event_dialog.default_mail_body')
                    : ''),
              }),
            },
            {
              validators: this.automationsValidationService.automatedActionValidator(
                this.accessoryDataService.jobItemData(),
              ),
            },
          ),
        );
        break;
      case AutomationActionType.CandidateShare:
        this.form.controls.actions.push(
          this.fb.nonNullable.group(
            {
              ...commonActionFields,
              data: this.fb.nonNullable.group({
                adminIds: this.fb.control(data?.adminIds || []),
                messageJson: this.fb.control(data?.messageJson || null),
              }),
            },
            {
              validators: this.automationsValidationService.automatedActionValidator(
                this.accessoryDataService.jobItemData(),
              ),
            },
          ),
        );
        break;
      case AutomationActionType.CandidateUnshare:
        this.form.controls.actions.push(
          this.fb.nonNullable.group(
            {
              ...commonActionFields,
              data: this.fb.nonNullable.group({
                adminIds: this.fb.control(data?.adminIds || []),
              }),
            },
            {
              validators: this.automationsValidationService.automatedActionValidator(
                this.accessoryDataService.jobItemData(),
              ),
            },
          ),
        );
        break;
      case AutomationActionType.JobAddTeamMembers:
        this.form.controls.actions.push(
          this.fb.nonNullable.group(
            {
              ...commonActionFields,
              data: this.fb.nonNullable.group({
                adminIds: this.fb.control(data?.adminIds || []),
              }),
            },
            {
              validators: this.automationsValidationService.automatedActionValidator(
                this.accessoryDataService.jobItemData(),
              ),
            },
          ),
        );
        break;
      case AutomationActionType.JobChangeStatus:
        this.form.controls.actions.push(
          this.fb.nonNullable.group(
            {
              ...commonActionFields,
              data: this.fb.nonNullable.group({
                status: this.fb.control(data?.status || null),
              }),
            },
            {
              validators: this.automationsValidationService.automatedActionValidator(
                this.accessoryDataService.jobItemData(),
              ),
            },
          ),
        );
        break;
      case AutomationActionType.JobSubmitToJobBoards:
        this.form.controls.actions.push(
          this.fb.nonNullable.group(
            {
              ...commonActionFields,
              data: this.fb.nonNullable.group({
                jobBoards: this.fb.control(data?.jobBoards || []),
              }),
            },
            {
              validators: this.automationsValidationService.automatedActionValidator(
                this.accessoryDataService.jobItemData(),
              ),
            },
          ),
        );
        break;
      default:
        this.form.controls.actions.push(
          this.fb.nonNullable.group(
            { ...commonActionFields },
            {
              validators: this.automationsValidationService.automatedActionValidator(
                this.accessoryDataService.jobItemData(),
              ),
            },
          ),
        );
    }
  }

  public cleanIncompatibleActions(): void {
    const actionsFormArray = <AutomationActionForm[]>this.form.controls.actions.controls;
    const currentActionOptions = this.automationOptionsService.currentActionOptions();
    const indexesToDelete: number[] = [];
    actionsFormArray.forEach((action, index) => {
      const actionType = action.controls.type.value;
      if (!currentActionOptions.some(actionOption => actionOption.type === actionType)) {
        indexesToDelete.unshift(index);
      }
    });

    indexesToDelete.forEach(index => {
      this.deleteAction(index);
    });
  }

  public cleanIncompatibleConditions(): number[] {
    const conditionsFormArray = <AutomationConditionForm[]>this.form.controls.conditions.controls;
    const currentConditionOptions = this.automationOptionsService.currentConditionsOptions();
    const indexesToDelete: number[] = [];
    conditionsFormArray.forEach((condition, index) => {
      const conditionType = condition.controls.type.value;
      if (
        !currentConditionOptions.some(conditionOption => conditionOption.type === conditionType)
      ) {
        indexesToDelete.unshift(index);
      }
    });

    indexesToDelete.forEach(index => {
      this.deleteCondition(index);
    });
    return indexesToDelete;
  }

  public deleteAction(index: number): void {
    this.form.controls.actions.removeAt(index);
  }

  public deleteCondition(index: number): void {
    this.form.controls.conditions.removeAt(index);
  }

  private removeAllControls(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(controlName => {
      formGroup.removeControl(controlName);
    });
  }
}
